import { useDepartment } from '../contexts/DepartmentContext';
import { applyVatIfNeeded } from '../constants/vat';
import { formatCurrencyExt } from '../utils/formatCurrency';

/**
 * Money formatting for the whole client.
 *
 * ═════════════════════════════════════════════════════════════════════════════
 * 🔻 VAT DISPLAY IS OFF — REMOVED FROM THE UI 2026-08-08, BY PRODUCT DECISION
 * ═════════════════════════════════════════════════════════════════════════════
 *
 * Product owner, verbatim: *"actualy remove VAT the reason is deeper. differnt
 * items demaded differnt vat rates etc so by not having vat the deparment can
 * just decided if they type in ex vat proices or inc vat prices. We can always
 * implement vat later? Kepe the code but remove vat for now."*
 *
 * 🔑 **READ THE REASON BEFORE RESTORING ANYTHING HERE. IT IS NOT THAT THE
 * FEATURE WAS AWKWARD — IT IS THAT IT WAS WRONG IN PRINCIPLE.**
 * `productions.include_vat` is a SINGLE BOOLEAN acting as ONE display
 * multiplier across an entire production. UK VAT has several rates — standard
 * (20%), reduced (5%), zero-rated and exempt — and **the rate varies PER LINE
 * ITEM**. A per-production boolean therefore cannot express the tax on a real
 * order: switching it on rendered confidently incorrect Inc-VAT figures for
 * anything not at the standard rate, with no indication that the number was
 * wrong. A wrong number presented as a right one is worse than no number.
 *
 * Departments will instead decide for themselves whether they type ex- or
 * inc-VAT prices into the app.
 *
 * ⚠️ **So "just flip it back on" is NOT the fix if VAT is wanted again.** A real
 * implementation needs a rate PER ITEM (or at least per supplier/category), not
 * this flag. What is kept below is the arithmetic and the plumbing so that work
 * starts from a working base — it is not a feature waiting to be re-enabled.
 *
 * ── WHAT WAS REMOVED, AND WHAT WAS DELIBERATELY KEPT ──
 * REMOVED: the `Include VAT (20%)` checkbox on the production form, the VAT
 * badge (`VatModeBanner`, under `components/`) from all seven pages that had it,
 * 🪤 written that way ON PURPOSE — `scripts/check-bloat.js` decides "is this file
 * imported?" with a bare `content.includes('/<basename>' + quote)` over every
 * file, so the natural phrasing (the folder name, a slash, then the component
 * name, in backticks) inside THIS COMMENT
 * made the scanner believe the component was still imported and silently
 * suppressed its own unused-file warning. Comments read as code here; see
 * CLAUDE.md's "every textual scanner reads COMMENTS as code". The
 * Subtotal/VAT/Grand-Total breakdowns on the order form and order page, and
 * every `(Ex VAT)` / `(Inc VAT)` label — see the note on labels below.
 * KEPT: `constants/vat.js` (`VAT_RATE`, `applyVatIfNeeded`), `formatCurrencyExt`,
 * the `productions.include_vat` COLUMN on both engines, and the API round-trip
 * through `POST`/`PUT /api/productions` — which still accept and preserve the
 * field even though nothing in the UI writes it any more.
 *
 * 🚨 **WHY THIS IS A HARD `false` AND NOT JUST "THE BADGE WAS DELETED".**
 * `formatMoney` is the money formatter for ~50 call sites. The column still
 * holds whatever it held, so if this hook kept reading it, any production
 * already saved with `include_vat = 1` would go on quietly multiplying every
 * displayed figure by 1.20 — with the badge that used to announce it now gone.
 * That is strictly worse than the feature was. **Removing the surface without
 * neutralising the multiplier would have been the actual bug.**
 *
 * ── LABELS: WHY `vatLabel` NO LONGER EXISTS ──
 * It is not merely unused — it would now be a FALSE claim. The app used to
 * guarantee stored values were ex-VAT, so "(Ex VAT)" was true. Under the
 * owner's decision a department may type inc-VAT prices, and **nothing records
 * which they chose**, so no screen can honestly label a figure either way.
 * Removing the key means no surface can accidentally reintroduce the claim.
 *
 * To re-enable a like-for-like display (NOT recommended — see above), set
 * VAT_DISPLAY_ENABLED to true and restore the badge and labels from the revert
 * of the commit that removed them.
 */
const VAT_DISPLAY_ENABLED = false;

export function useVatMode() {
  // The context is still consumed so the hook keeps its identity as a hook and
  // its call sites are unchanged. `activeProduction` is deliberately NOT read:
  // see the hard-`false` note above.
  useDepartment();
  const includeVat = VAT_DISPLAY_ENABLED;
  return {
    includeVat,
    applyVat: (amount) => applyVatIfNeeded(amount, includeVat),
    formatMoney: (amount, opts) => formatCurrencyExt(amount, includeVat, opts),
  };
}
